import { z } from "zod";
import { DEFAULT_QUEUE_SHARD_COUNT } from "./runtime-queue.js";
import { runIdSchema, taskRunEventPayloadSchema, runErrorCodeSchema, } from "./primitives.js";
import { twinExecutionCertificateRefsSchema } from "./twin-execution-certificates.js";
import { modelGatewayErrorOriginSchema, modelGatewayFailureKindSchema, providerFinishReasonSchema } from "./model-gateway.js";
import { candidateComponentBuildAttestationSchema } from "./candidate-build.js";
export const workerExecutionLaneSchema = z.string()
    .regex(/^(?:production|candidate:[0-9a-f]{7,64}|evaluation:[0-9a-f]{64})$/u);
export const workerClaimInputSchema = z.object({
    workerId: z.string().trim().min(1),
    /** Stable across retries until the worker has received the whole claim response. */
    claimRequestId: z.string().uuid().optional(),
    executionLane: workerExecutionLaneSchema.default("production"),
    leaseMs: z.number().int().positive().max(5 * 60 * 1000),
    limit: z.number().int().positive().max(100),
    queueShardCount: z.number().int().positive().max(256).default(DEFAULT_QUEUE_SHARD_COUNT),
    shardStride: z.number().int().positive().max(256).default(1),
    shardOffset: z.number().int().min(0).default(0),
    shardCursor: z.number().int().min(0).default(0),
    runRoles: z.array(z.enum(["task_agent", "retrieval_agent"])).min(1).max(2).optional(),
});
export const attestCandidateRunAttemptInputSchema = z.object({
    runId: runIdSchema,
    workerId: z.string().trim().min(1),
    attemptNumber: z.number().int().positive(),
    executionLane: workerExecutionLaneSchema.refine((lane) => lane.startsWith("candidate:")),
    attestations: z.array(candidateComponentBuildAttestationSchema).length(2),
}).strict().superRefine((input, context) => {
    const components = new Set(input.attestations.map((item) => item.component));
    if (components.size !== 2)
        context.addIssue({ code: "custom", path: ["attestations"], message: "worker and runtime attestations are required" });
});
export const workerHeartbeatInputSchema = z.object({
    runId: runIdSchema,
    workerId: z.string().trim().min(1),
    attemptNumber: z.number().int().positive(),
    leaseMs: z.number().int().positive().max(5 * 60 * 1000),
});
export const appendTaskRunEventInputSchema = z.object({
    runId: runIdSchema,
    workerId: z.string().trim().min(1),
    attemptNumber: z.number().int().positive(),
    kind: z.string().trim().min(1).max(100),
    payload: taskRunEventPayloadSchema,
});
export const appendTaskRunEventsInputSchema = z.object({
    runId: runIdSchema,
    workerId: z.string().trim().min(1),
    attemptNumber: z.number().int().positive(),
    // Idempotency key for the batch: replays of an already-recorded
    // (runId, batchKey) acknowledge without re-inserting events.
    batchKey: z.string().trim().min(1).max(64).optional(),
    events: z
        .array(z.object({
        kind: z.string().trim().min(1).max(100),
        payload: taskRunEventPayloadSchema,
    }))
        .min(1)
        .max(100),
});
export const workerRunEventsInputSchema = z.union([
    appendTaskRunEventInputSchema,
    appendTaskRunEventsInputSchema,
]);
export const completeTaskRunInputSchema = z.object({
    runId: runIdSchema,
    workerId: z.string().trim().min(1),
    attemptNumber: z.number().int().positive(),
    /* Empty is a valid completion: a turn that ends on a completed tool call
     * with no closing text (the user asked for a post and nothing else). */
    outputText: z.string().trim(),
    certificateRefs: twinExecutionCertificateRefsSchema.optional(),
});
export const failTaskRunInputSchema = z.object({
    runId: runIdSchema,
    workerId: z.string().trim().min(1),
    attemptNumber: z.number().int().positive(),
    errorMessage: z.string().trim().min(1),
    errorCode: runErrorCodeSchema.optional(),
    /** Where a model call's failure came from, from the model gateway's envelope (model-gateway.ts). */
    errorOrigin: modelGatewayErrorOriginSchema.optional(),
    /** What it was: an upstream kind (provider_unreachable, …) or the gateway's own error code. */
    errorKind: modelGatewayFailureKindSchema.optional(),
    /** Why the provider ended its reply, for `provider_stopped` (Gemini's finishReason: SAFETY, …). */
    errorFinishReason: providerFinishReasonSchema.optional(),
    /**
     * Whether the failed attempt may be run again without repeating anything: false once it ran a
     * tool with a side effect (a write, writeback or send) or asked for an approval. A worker that
     * does not say leaves the tenant's retry rule as it was.
     */
    retrySafe: z.boolean().optional(),
});
export const controlPlaneMetricsSchema = z.object({
    generatedAt: z.number(),
    queuedCount: z.number(),
    runningCount: z.number(),
    completedCount: z.number(),
    failedCount: z.number(),
    staleRunningCount: z.number(),
    recentCompletedCount: z.number(),
    recentFailedCount: z.number(),
    oldestQueuedAgeMs: z.number().optional(),
    oldestRunningAgeMs: z.number().optional(),
});
export const workerMutationResultSchema = z.object({
    ok: z.boolean(),
    // Refusal codes belong to the endpoint (including completion/evidence checks).
    // Any refusal is a failure, even when a newer server introduces another code.
    reason: z.string().trim().min(1).optional(),
}).refine((value) => value.ok ? value.reason === undefined : value.reason !== undefined, {
    message: "A refused worker mutation requires a reason; an acknowledgement must not contain one.",
});
// Post-deploy claim canary (incident 2026-07-02). A synthetic, LLM-free run
// that proves the claim SQL + claim-time context lanes + completion path work
// after a deploy. The scenario marker below is load-bearing: the worker
// short-circuits it (no runtime handoff) and the control plane skips memory
// curation for it.
export const CLAIM_CANARY_SCENARIO = "claim-canary";
export const CLAIM_CANARY_MARKER_OUTPUT = "claim-canary-ok";
export const ELECTRIC_RUNTIME_CANARY_SCENARIO = "electric-runtime-canary-v1";
export const ELECTRIC_RUNTIME_CANARY_PROTOCOL_VERSION = 1;
export const ELECTRIC_RUNTIME_CANARY_MARKER_OUTPUT = "electric-runtime-canary-v1-ok";
export const claimCanaryStartResultSchema = z.object({
    runId: runIdSchema,
    taskId: z.string().trim().min(1),
    scenario: z.literal(CLAIM_CANARY_SCENARIO),
});
export const claimCanaryStatusSchema = z.object({
    runId: runIdSchema,
    status: z.string(),
    scenario: z.string().nullable(),
    leaseOwner: z.string().nullable(),
    startedAt: z.number().nullable(),
    completedAt: z.number().nullable(),
    outputMatchesMarker: z.boolean().optional(),
});
export const electricRuntimeCanaryStartResultSchema = z.object({
    runId: runIdSchema,
    taskId: z.string().trim().min(1),
    sessionId: z.string().trim().min(1),
    scenario: z.literal(ELECTRIC_RUNTIME_CANARY_SCENARIO),
});
